import { Bell, ExternalLink, CheckCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useMyPricingNotifications, useMarkPricingNotificationRead } from "@/features/tenders/hooks/useTenders";
import type { MyPricingNotification } from "@/features/tenders/types";
import { Button } from "@/components/ui/Button";

function fmt(n: number): string {
  const [int, dec] = Math.abs(n).toFixed(2).split(".");
  const intFormatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");
  return (n < 0 ? "-" : "") + intFormatted + "," + dec;
}

export function PricingNotificationsPage() {
  const { data: notifications = [], isLoading } = useMyPricingNotifications();
  const markRead = useMarkPricingNotificationRead();

  const unread = notifications.filter((n) => !n.is_read);
  const read = notifications.filter((n) => n.is_read);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        Ładowanie...
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Bell size={20} />
          Powiadomienia — propozycje wyceny
        </h1>
        {unread.length > 0 && (
          <Button
            size="sm"
            variant="secondary"
            loading={markRead.isPending}
            onClick={() => unread.forEach((n) => markRead.mutate(n.id))}
          >
            <CheckCheck size={14} /> Oznacz wszystkie jako przeczytane
          </Button>
        )}
      </div>

      {notifications.length === 0 && (
        <div className="text-center py-16 text-gray-400 dark:text-gray-500">
          Brak powiadomień.
        </div>
      )}

      {unread.length > 0 && (
        <section className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Nieprzeczytane ({unread.length})
          </p>
          {unread.map((n) => <NotifCard key={n.id} n={n} onRead={() => markRead.mutate(n.id)} />)}
        </section>
      )}

      {read.length > 0 && (
        <section className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Przeczytane
          </p>
          {read.map((n) => <NotifCard key={n.id} n={n} />)}
        </section>
      )}
    </div>
  );
}

function NotifCard({ n, onRead }: { n: MyPricingNotification; onRead?: () => void }) {
  return (
    <div className={`rounded-lg border p-4 space-y-2 ${
      !n.is_read
        ? "border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20"
        : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
            {n.tender_title}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Tura {n.round_number} · Propozycja:{" "}
            <span className="font-mono font-semibold">{fmt(Number(n.proposal_value))} PLN</span>
          </p>
          {n.message && (
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 italic">"{n.message}"</p>
          )}
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            od {n.sender_name} · {new Date(n.created_at).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to={`/tenders/${n.tender_id}`}
            className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] dark:text-blue-400 hover:underline"
          >
            <ExternalLink size={12} /> Otwórz
          </Link>
          {onRead && (
            <button
              onClick={onRead}
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              Oznacz jako przeczytane
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
